import { useState } from 'react';

const facts = [
  {
    icon: 'ri-global-line',
    title: 'Wereldwijd Bekend',
    text: 'Het boze oog is een vloek die wereldwijd wordt erkend. Al duizenden jaren lang beschermen culturen zich hiertegen met blauwe poeders en rituelen.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Krachtige Bescherming',
    text: 'Blauw poeder wordt aangebracht op het voorhoofd, de polsen, handen en voeten als beschermend teken. Ook wordt het gebruikt om huizen en ruimtes te reinigen van negativiteit.',
  },
  {
    icon: 'ri-sparkling-line',
    title: 'Spirituele Kleur',
    text: 'De kleur blauw heeft een diepe spirituele betekenis — het wordt beschouwd als de kleur van spirituele bescherming, positieve krachten en het afweren van kwade energieën.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Beschermt Kwetsbaren',
    text: 'Het boze oog treft in het bijzonder kinderen en zwangere vrouwen hard. Blauw poeder biedt hen extra spirituele bescherming en creëert een positieve aura.',
  },
];

const names = [
  { culture: 'Surinaams', name: 'Ogri Ai', flag: '🇸🇷' },
  { culture: 'Hindoestaans', name: 'Nazar', flag: '🪬' },
  { culture: 'Spaans', name: 'Azul', flag: '🇪🇸' },
];

export const BlueProtectInfo = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <section id="blue-protect-info" className="w-full py-16 bg-gradient-to-b from-[#050010] via-[#0a0820] to-[#050010] border-y border-violet-500/10">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Beschermende Kracht ✦</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Over{' '}
            <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">
              Blue Protect
            </span>
          </h2>
          <p className="text-white/50 text-sm max-w-2xl mx-auto leading-relaxed">
            Blue Protect is gebaseerd op een eeuwenoud gebruik van blauw poeder voor spirituele bescherming — geworteld in Surinaamse cultuur en wereldwijd erkend.
          </p>
        </div>

        {/* World Names Block */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {names.map(n => (
            <div
              key={n.name}
              className="flex items-center gap-3 px-5 py-3 bg-[#110020]/80 border border-violet-500/20 rounded-full"
            >
              <span className="text-lg">{n.flag}</span>
              <div>
                <p className="text-white/40 text-xs">{n.culture}</p>
                <p className="text-violet-300 font-semibold text-sm">{n.name}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Intro Block */}
        <div className="relative rounded-2xl overflow-hidden border border-violet-500/20 mb-10">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/0dacfaa6efbdb4b00828fb6ecc6e9fac.jpg"
              alt="Blue Protect achtergrond"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050010]/95 via-[#050010]/80 to-[#050010]/60" />
          </div>
          <div className="relative z-10 p-8 lg:p-12 max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-violet-600/20 border border-violet-500/30 shrink-0">
                <i className="ri-eye-off-line text-violet-400 text-lg" />
              </div>
              <h3 className="text-white font-bold text-xl">Wat is het Boze Oog?</h3>
            </div>
            <p className="text-white/65 text-sm leading-relaxed mb-4">
              Het <strong className="text-violet-300">Boze Oog</strong> — ook wel <em>Ogri Ai</em> in Surinaamse cultuur,{' '}
              <em>Nazar</em> bij Hindoestanen en <em>Azul</em> bij Spaanse volkeren — is een vloek die al millennia lang de mensheid treft.
              Deze vloek veroorzaakt lichamelijke klachten, pech, slaperigheid, misselijkheid, rusteloosheid en overmatig huilen.
            </p>
            <p className="text-white/65 text-sm leading-relaxed">
              Het bestaan van het boze oog is wereldwijd bekend en dit geloof bestaat al sinds de oudheid.
              Bescherming, in de vorm van blauw poeder, is noodzakelijk om de risico&apos;s en effecten van het boze oog te voorkomen —
              in het bijzonder voor kinderen en zwangere vrouwen.
            </p>
          </div>
        </div>

        {/* Facts Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {facts.map(fact => (
            <div
              key={fact.title}
              className="bg-[#110020]/60 border border-violet-500/15 rounded-2xl p-6 hover:border-violet-500/35 transition-all duration-300"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-violet-600/15 border border-violet-500/20 mb-4">
                <i className={`${fact.icon} text-violet-400 text-lg`} />
              </div>
              <h4 className="text-white font-semibold text-sm mb-2">{fact.title}</h4>
              <p className="text-white/45 text-xs leading-relaxed">{fact.text}</p>
            </div>
          ))}
        </div>

        {/* Expandable Deep Dive */}
        <div className="bg-[#110020]/60 border border-violet-500/15 rounded-2xl overflow-hidden">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between p-6 cursor-pointer hover:bg-violet-500/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-violet-600/15 border border-violet-500/20 shrink-0">
                <i className="ri-book-open-line text-violet-400" />
              </div>
              <span className="text-white font-semibold text-sm">Surinaamse Tradities &amp; Oorsprong van Blauw Poeder</span>
            </div>
            <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-violet-400 text-lg transition-transform`} />
          </button>

          {expanded && (
            <div className="px-6 pb-8 border-t border-violet-500/10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6">
                <div>
                  <h4 className="text-violet-300 font-semibold text-sm mb-3 flex items-center gap-2">
                    <i className="ri-flask-line text-violet-400" />
                    Traditionele Toepassing
                  </h4>
                  <p className="text-white/55 text-xs leading-relaxed mb-4">
                    In de Surinaamse cultuur wordt blauw poeder veel gebruikt voor bescherming tegen kwade geesten en negatieve energieën.
                    Dit blauwe poeder staat bekend als &quot;blauw buskruit&quot; of &quot;blauw poeder&quot; en is traditioneel gemaakt van gemalen indigofera-planten.
                    Het wordt vaak gebruikt bij spirituele rituelen en ceremonies.
                  </p>
                  <p className="text-white/55 text-xs leading-relaxed">
                    Het blauw poeder wordt doorgaans op het lichaam aangebracht, met name op het voorhoofd, de handen en de voeten,
                    als beschermend teken. Het wordt ook gebruikt om huizen en andere ruimtes te reinigen en te beschermen tegen negativiteit.
                  </p>
                </div>
                <div>
                  <h4 className="text-violet-300 font-semibold text-sm mb-3 flex items-center gap-2">
                    <i className="ri-drop-line text-violet-400" />
                    Manieren van Gebruik
                  </h4>
                  <ul className="space-y-2">
                    {[
                      'Aanbrengen op de polsen en achter de oren',
                      'Op de nek als beschermend teken',
                      'Oplossen in badwater voor volledige reiniging',
                      'Verspreiden in drempelgebieden van het huis',
                      'Gebruik bij spirituele rituelen en ceremonies',
                    ].map(item => (
                      <li key={item} className="flex items-start gap-2 text-white/50 text-xs">
                        <i className="ri-checkbox-circle-line text-violet-400 mt-0.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 p-4 bg-violet-600/10 border border-violet-500/20 rounded-xl">
                    <p className="text-violet-300 text-xs font-semibold mb-1">Diep geworteld in cultuur</p>
                    <p className="text-white/45 text-xs leading-relaxed">
                      Het gebruik van blauw poeder is diepgeworteld in de Surinaamse cultuur en is een belangrijk onderdeel van
                      spirituele praktijken en folklore. Het dient als een manier om zichzelf en anderen te beschermen
                      tegen negatieve invloeden en een positieve spirituele sfeer te creëren.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </section>
  );
};
